import * as React from "react"
import { Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { Option } from "@/types/inertia"

// The filter row above an admin list (M7). Filters live in the query string:
// every change is a GET to the same page with `preserveState`, so the search
// box keeps its focus and the list re-renders from the server's props.
// Selects apply at once; the search box applies on Enter, on "Search", or a
// moment after typing stops.

export type FilterField =
  | { kind: "search"; name: string; label: string; placeholder?: string }
  | { kind: "select"; name: string; label: string; options: Option[]; allLabel: string }

type FilterValues = Record<string, string | null | undefined>

const SEARCH_DELAY_MS = 400

function queryFrom(values: FilterValues) {
  const query: Record<string, string> = {}
  for (const [key, value] of Object.entries(values)) {
    if (value !== null && value !== undefined && value !== "") query[key] = value
  }
  return query
}

export function FilterBar({
  path,
  values,
  fields,
  idPrefix,
}: {
  /** The list's own path, e.g. "/admin/wines". */
  path: string
  /** The server's view of the filters (the page's `filters` prop). */
  values: FilterValues
  fields: FilterField[]
  idPrefix: string
}) {
  const searchField = fields.find((field) => field.kind === "search")
  const serverSearch = searchField ? (values[searchField.name] ?? "") : ""
  const [search, setSearch] = React.useState(serverSearch)
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const sent = React.useRef<string | null>(null)

  // Follow the server when the search changes from elsewhere, but never
  // overwrite what is being typed with the answer to an earlier keystroke.
  React.useEffect(() => {
    if (sent.current !== null && serverSearch === sent.current) return
    setSearch(serverSearch)
  }, [serverSearch])
  React.useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const apply = React.useCallback(
    (changes: FilterValues) => {
      if (timer.current) clearTimeout(timer.current)
      if (searchField && searchField.name in changes) sent.current = changes[searchField.name] ?? ""
      router.get(path, queryFrom({ ...values, ...changes }), {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    },
    [path, values, searchField],
  )

  const onSearchChange = (name: string, value: string) => {
    setSearch(value)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => apply({ [name]: value.trim() }), SEARCH_DELAY_MS)
  }

  const active = fields.some((field) => {
    const value = values[field.name]
    return value !== null && value !== undefined && value !== ""
  })

  return (
    <form
      role="search"
      className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
      onSubmit={(event) => {
        event.preventDefault()
        if (searchField) apply({ [searchField.name]: search.trim() })
      }}
    >
      {fields.map((field) => {
        const id = `${idPrefix}-${field.name}`
        if (field.kind === "search") {
          return (
            <div key={field.name} className="space-y-2 sm:min-w-[14rem] sm:flex-1">
              <label htmlFor={id}>{field.label}</label>
              <div className="flex gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                  />
                  <Input
                    id={id}
                    type="search"
                    className="form-control-large pl-9"
                    placeholder={field.placeholder}
                    value={search}
                    onChange={(event) => onSearchChange(field.name, event.target.value)}
                  />
                </div>
                <Button type="submit" variant="secondary" size="lg">
                  Search
                </Button>
              </div>
            </div>
          )
        }
        return (
          <div key={field.name} className="space-y-2 sm:w-48">
            <label htmlFor={id}>{field.label}</label>
            <Select
              id={id}
              className="form-control-large"
              value={values[field.name] ?? ""}
              onChange={(event) => apply({ [field.name]: event.target.value })}
            >
              <option value="">{field.allLabel}</option>
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        )
      })}
      {active && (
        <Button asChild variant="ghost" size="lg">
          <Link href={path} preserveScroll>
            Clear filters
          </Link>
        </Button>
      )}
    </form>
  )
}
